/* useTheme — Doux (sombre) / Clair (clair). API inchangée par rapport au Nuxt
 * (spec §4.5), mais UNE SEULE implémentation : le script inline d'`index.html`
 * a déjà posé `data-theme` sur <html> avant le premier rendu, ce singleton s'y
 * initialise (plus de `onMounted`, plus de plugin client).
 *
 * Applique :
 *  - data-theme="doux|clair" sur <html> (driver des variables du design system)
 *  - la classe `dark` sur <html> quand doux est actif (pilote @custom-variant)
 */

import { readonly, ref } from 'vue';
import type { Ref } from 'vue';
import { Theme } from '@/shared/enums';

const STORAGE_KEY = 'theme';

/** Valeurs héritées d'avant le renommage doux/clair. */
const LEGACY_VALUES: Record<string, Theme> = { dark: Theme.Doux, light: Theme.Clair };

function readStored(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === Theme.Doux || stored === Theme.Clair) return stored;
    if (stored && LEGACY_VALUES[stored]) return LEGACY_VALUES[stored];
  } catch {
    // Stockage bloqué (navigation privée, cookies tiers refusés) : on ignore.
  }
  return null;
}

/** Le thème réellement posé sur <html> par le script anti-FOUC, sinon le stockage. */
function resolveInitialTheme(): Theme {
  const attribute = document.documentElement.getAttribute('data-theme');
  if (attribute === Theme.Doux || attribute === Theme.Clair) return attribute;
  return readStored() ?? Theme.Doux;
}

const theme: Ref<Theme> = ref(resolveInitialTheme());

function applyTheme(value: Theme): void {
  const html = document.documentElement;
  html.setAttribute('data-theme', value);
  html.classList.toggle('dark', value === Theme.Doux);
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Stockage bloqué : le thème reste appliqué, simplement non persisté.
  }
}

// Le script inline ne persiste rien : on réaligne l'attribut, la classe `dark`
// et le stockage dès le premier import du composable.
applyTheme(theme.value);

function setTheme(value: Theme): void {
  theme.value = value;
  applyTheme(value);
}

function toggleTheme(): void {
  setTheme(theme.value === Theme.Doux ? Theme.Clair : Theme.Doux);
}

export interface ThemeApi {
  theme: Readonly<Ref<Theme>>;
  toggleTheme(): void;
  setTheme(value: Theme): void;
}

export function useTheme(): ThemeApi {
  return { theme: readonly(theme) as Readonly<Ref<Theme>>, toggleTheme, setTheme };
}
